import type { RequiredEnrollmentDocument } from '@/api/enrollment-required-documents'
import type { EnrollmentDocumentType } from '@/api/types/enrollment'
import { getUploadConfig } from '../uploadDocumentConfig'
import type { FileState, UploadedDocumentsByType, VisibleUploadDocument } from './types'

type UploadedDocument = {
  documentType: string
  fileName?: string | null
  status?: string | null
}

export function getVisibleUploadDocuments(
  requiredDocuments: RequiredEnrollmentDocument[] | undefined
): VisibleUploadDocument[] {
  return (requiredDocuments ?? []).filter(
    document =>
      document.documentType !== 'DRIVERS_LICENSE' &&
      !(document.uploaded && document.uploadedDocumentId == null)
  )
}

export function buildUploadedDocumentsByType(
  documents: UploadedDocument[] | undefined
): UploadedDocumentsByType {
  const uploaded = new Map<EnrollmentDocumentType, string>()

  for (const document of documents ?? []) {
    if (document.status === 'REJECTED') continue
    if (!document.fileName) continue
    uploaded.set(document.documentType as EnrollmentDocumentType, document.fileName)
  }

  return uploaded
}

export function buildUploadedDocumentFiles(
  documents: UploadedDocument[] | undefined
): Partial<FileState> {
  const nextFiles: Partial<FileState> = {}

  for (const document of documents ?? []) {
    if (document.status === 'REJECTED') continue

    if (document.documentType === 'SOCIAL_SECURITY') nextFiles.ssn = document.fileName ?? undefined
    if (document.documentType === 'HIGH_SCHOOL_DIPLOMA' || document.documentType === 'GED') {
      nextFiles.diploma = document.fileName ?? undefined
    }
    if (document.documentType === 'OTHER') nextFiles.mvr = document.fileName ?? undefined
  }

  return nextFiles
}

export function isUploadDocumentCompleted(
  document: RequiredEnrollmentDocument,
  uploadedDocumentsByType: UploadedDocumentsByType
) {
  if (document.uploaded && document.uploadedStatus !== 'REJECTED') {
    return true
  }

  return uploadedDocumentsByType.has(document.documentType)
}

export function getUploadedDocumentFileName({
  document,
  files,
  uploadedDocumentsByType
}: {
  document: VisibleUploadDocument
  files: FileState
  uploadedDocumentsByType: UploadedDocumentsByType
}) {
  const config = getUploadConfig(document)

  return (
    uploadedDocumentsByType.get(document.documentType) ??
    files[config.key] ??
    (document.uploaded ? document.name : '')
  )
}
